import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import { RefereeCertification } from '@/models/tournament';
import type { RefereeView } from '@/models/tournament';

interface RefereeListPanelProps {
  referees: RefereeView[];
  selectedReferee: RefereeView | null;
  onSelectReferee: (referee: RefereeView) => void;
}

const RefereeListPanel: React.FC<RefereeListPanelProps> = ({ referees, selectedReferee, onSelectReferee }) => {
  const { t } = useTranslation('tournament');

  const certificationKeys: Record<RefereeCertification, string> = {
    [RefereeCertification.NationalLevelA]: 'National Level A',
    [RefereeCertification.AFCElite]: 'AFC Elite',
    [RefereeCertification.FIFAInternational]: 'FIFA International',
  };

  if (referees?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center" data-testid="referee-empty-state">
        <Icon name="Users" size={48} className="mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('No referees found')}</h3>
        <p className="text-sm text-gray-600">{t('Try adjusting your filters')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-lg p-4" data-testid="referee-list-panel">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
          <Icon name="Users" size={20} className="me-2 text-primary" />
          {t('Available Referees')}
          <span className="ms-auto text-sm font-normal text-gray-500">({referees?.length})</span>
        </h2>

        <div className="space-y-3 max-h-[calc(100vh-300px)] overflow-y-auto pe-2">
          {referees?.map((referee) => (
            <div
              key={referee?.id}
              onClick={() => onSelectReferee(referee)}
              className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                selectedReferee?.id === referee?.id
                  ? 'border-primary bg-primary/5 shadow-md'
                  : 'border-border hover:border-primary/50 hover:shadow-sm'
              }`}
              data-testid={`referee-card-${referee?.id}`}
            >
              {/* Profile Header */}
              <div className="flex items-start space-x-3 rtl:space-x-reverse mb-3">
                {referee?.photoUrl && (
                  <img
                    src={referee?.photoUrl}
                    alt={`${referee?.name} profile photo`}
                    className="w-12 h-12 rounded-full object-cover border-2 border-border"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900 truncate">
                    {referee?.name}
                  </h3>
                  <p className="text-xs text-gray-500">{referee?.id}</p>
                  <div className="flex items-center mt-1">
                    <Icon name="MapPin" size={12} className="text-gray-400 me-1" />
                    <span className="text-xs text-gray-600">{referee?.location}</span>
                  </div>
                </div>
              </div>

              {/* Certification */}
              <div className="bg-blue-50 border border-blue-200 rounded px-2 py-1.5 mb-3">
                <div className="flex items-center">
                  <Icon name="Award" size={12} className="text-blue-600 me-1.5" />
                  <span className="text-xs font-medium text-blue-900">{t(certificationKeys[referee?.certification] ?? 'Unknown')}</span>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-gray-50 rounded px-2 py-1.5">
                  <p className="text-xs text-gray-500">{t('Matches')}</p>
                  <p className="text-sm font-semibold text-gray-900">{referee?.totalMatches}</p>
                </div>
                <div className="bg-gray-50 rounded px-2 py-1.5">
                  <p className="text-xs text-gray-500">{t('Rating')}</p>
                  <div className="flex items-center">
                    <Icon name="Star" size={12} className="text-yellow-500 me-1" />
                    <p className="text-sm font-semibold text-gray-900">{referee?.rating}</p>
                  </div>
                </div>
              </div>

              {/* Contact Info (shown when selected) */}
              {selectedReferee?.id === referee?.id && (
                <div className="mt-3 pt-3 border-t border-border space-y-1.5">
                  <div className="flex items-center text-xs text-gray-600">
                    <Icon name="Phone" size={12} className="me-2 text-gray-400" />
                    <span>{referee?.phone}</span>
                  </div>
                  <div className="flex items-center text-xs text-gray-600">
                    <Icon name="Mail" size={12} className="me-2 text-gray-400" />
                    <span className="truncate">{referee?.email}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RefereeListPanel;
